import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, IconButton,
  Paper, CircularProgress, Alert, Divider, Autocomplete,
} from "@mui/material";
import AddIcon           from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIcon     from "@mui/icons-material/ArrowBack";
import EditNoteIcon      from "@mui/icons-material/EditNote";
import api from "../../../services/api";

const C = {
  primary:   "#2563EB",
  primaryBg: "#EFF6FF",
  surface:   "#FFFFFF",
  surfaceAlt:"#F8FAFC",
  border:    "#E2E8F0",
  borderHov: "#CBD5E1",
  text1:     "#0F172A",
  text2:     "#475569",
  text3:     "#94A3B8",
  successBdr:"#BBF7D0",
  danger:    "#DC2626",
  dangerBg:  "#FEF2F2",
  dangerBdr: "#FECACA",
  infoBdr:   "#BFDBFE",
  warn:      "#D97706",
  warnBg:    "#FFFBEB",
  warnBdr:   "#FDE68A",
  urgentBg:  "#FFF7ED",
  urgentBdr: "#FDBA74",
  urgent:    "#EA580C",
};

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface,
    fontSize: "0.82rem", color: C.text1,
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.8rem", color: C.text3 },
  "& .MuiInputLabel-root.Mui-focused": { color: C.primary },
};

const SIFAT_OPTIONS = [
  { value: "biasa",    label: "Biasa",    color: C.text2,  bg: C.surfaceAlt, border: C.border },
  { value: "segera",   label: "Segera",   color: C.warn,   bg: C.warnBg,     border: C.warnBdr },
  { value: "mendesak", label: "Mendesak", color: C.urgent, bg: C.urgentBg,   border: C.urgentBdr },
];

const SATUAN_OPTIONS = [
  "pcs", "rim", "pak", "lusin",
];

function ItemRow({ item, index, total, onChange, onRemove, atkOptions }) {
  return (
    <Box sx={{ py: 1.5, borderBottom: index < total - 1 ? `1px solid ${C.border}` : "none" }}>
      {/* Desktop */}
      <Box sx={{
        display: { xs: "none", sm: "grid" },
        gridTemplateColumns: "32px 1fr 80px 130px 32px",
        gap: 1, alignItems: "center",
      }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: "6px",
          backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: C.primary }}>{index + 1}</Typography>
        </Box>

        <Autocomplete
          freeSolo
          size="small"
          options={atkOptions || []}
          value={item.item_name || ""}
          onInputChange={(e, newValue) => onChange(index, "item_name", newValue || "")}
          filterOptions={(options, state) => {
            if (state.inputValue.trim() === "") return [];
            return options.filter((opt) =>
              opt.toLowerCase().includes(state.inputValue.toLowerCase())
            );
          }}
          PaperComponent={(props) => (
            <Paper {...props} elevation={4} sx={{
              borderRadius: "8px", border: `1px solid ${C.border}`,
              mt: 0.5, fontSize: "0.82rem", boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
            }} />
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Pilih atau ketik baru..." required />
          )}
          sx={{
            ...inputBase,
            width: "100%",
            "& .MuiOutlinedInput-root": {
              ...inputBase["& .MuiOutlinedInput-root"],
              padding: "1px 8px !important",
            },
            "& .MuiOutlinedInput-input": {
              padding: "7px 4px !important",
            }
          }}
        />

        <TextField size="small" type="number" placeholder="Jml"
          value={item.quantity}
          onChange={(e) => onChange(index, "quantity", e.target.value)}
          inputProps={{ min: 1 }} sx={inputBase} />

        <TextField size="small" select
          value={item.satuan}
          onChange={(e) => onChange(index, "satuan", e.target.value)}
          SelectProps={{ native: true }}
          sx={inputBase}>
          {SATUAN_OPTIONS.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </TextField>

        <IconButton size="small" onClick={() => onRemove(index)} disabled={total === 1}
          sx={{
            width: 26, height: 26, borderRadius: "6px",
            color: total === 1 ? C.text3 : C.danger,
            "&:hover": { backgroundColor: total === 1 ? "transparent" : C.dangerBg },
          }}>
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Mobile */}
      <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{
            width: 24, height: 24, borderRadius: "6px",
            backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: C.primary }}>{index + 1}</Typography>
          </Box>
          <IconButton size="small" onClick={() => onRemove(index)} disabled={total === 1}
            sx={{
              width: 26, height: 26, borderRadius: "6px",
              color: total === 1 ? C.text3 : C.danger,
              "&:hover": { backgroundColor: total === 1 ? "transparent" : C.dangerBg },
            }}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Autocomplete
          freeSolo
          size="small"
          options={atkOptions || []}
          value={item.item_name || ""}
          onInputChange={(e, newValue) => onChange(index, "item_name", newValue || "")}
          filterOptions={(options, state) => {
            if (state.inputValue.trim() === "") return [];
            return options.filter((opt) =>
              opt.toLowerCase().includes(state.inputValue.toLowerCase())
            );
          }}
          PaperComponent={(props) => (
            <Paper {...props} elevation={4} sx={{
              borderRadius: "8px", border: `1px solid ${C.border}`,
              mt: 0.5, fontSize: "0.82rem", boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
            }} />
          )}
          renderInput={(params) => (
            <TextField {...params} placeholder="Pilih atau ketik baru..." required />
          )}
          sx={{
            ...inputBase,
            width: "100%",
            "& .MuiOutlinedInput-root": {
              ...inputBase["& .MuiOutlinedInput-root"],
              padding: "1px 8px !important",
            },
            "& .MuiOutlinedInput-input": {
              padding: "7px 4px !important",
            }
          }}
        />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          <TextField size="small" type="number" placeholder="Jumlah"
            value={item.quantity}
            onChange={(e) => onChange(index, "quantity", e.target.value)}
            inputProps={{ min: 1 }} sx={inputBase} />
          <TextField size="small" select
            value={item.satuan}
            onChange={(e) => onChange(index, "satuan", e.target.value)}
            SelectProps={{ native: true }}
            sx={inputBase}>
            {SATUAN_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </TextField>
        </Box>
      </Box>
    </Box>
  );
}

export default function CreateAtk() {
  const navigate = useNavigate();

  const [requesterName, setRequesterName] = useState("");
  const [department,    setDepartment]    = useState("");
  const [requestDate,   setRequestDate]   = useState(new Date().toISOString().slice(0, 10));
  const [usedFor,       setUsedFor]       = useState("");
  const [requestType,   setRequestType]   = useState("biasa");
  const [description,   setDescription]   = useState("");
  const [items,         setItems]         = useState([{ item_name: "", quantity: 1, satuan: "pcs" }]);

  const [pendingStatus, setPendingStatus] = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [success,       setSuccess]       = useState(false);
  const [atkOptions,    setAtkOptions]    = useState([]);

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); } catch { return null; }
  })();

  const isAdmin = window.location.pathname.startsWith("/admin") || window.location.pathname.startsWith("/superadmin");

  useEffect(() => {
    (async () => {
      try {
        const statusRes = await api.get("/statuses");
        const statuses  = Array.isArray(statusRes.data) ? statusRes.data : statusRes.data?.data ?? [];
        const pending   = statuses.find(s => s.name.toLowerCase() === "pending");
        if (pending) setPendingStatus(pending.id);

        const atkRes = await api.get("/atk-items", { params: { per_page: 9999 } });
        const atks = Array.isArray(atkRes.data?.data?.data) ? atkRes.data.data.data : (Array.isArray(atkRes.data?.data) ? atkRes.data.data : []);
        const names = [...new Set(atks.map(a => a.name))].filter(Boolean);
        setAtkOptions(names);
      } catch (err) { console.error(err); }
    })();
  }, []);

  const handleItemChange = (index, field, value) => {
    const updated = [...items]; updated[index][field] = value; setItems(updated);
  };
  const addItem    = () => setItems([...items, { item_name: "", quantity: 1, satuan: "pcs" }]);
  const removeItem = (index) => { if (items.length > 1) setItems(items.filter((_, i) => i !== index)); };

  const handleSubmit = async () => {
    setError(null);
    if (!requesterName) return setError("Nama pemohon harus diisi.");
    if (!department)    return setError("Departemen harus diisi.");
    if (!requestDate)   return setError("Tanggal pengajuan harus diisi.");
    if (!pendingStatus) return setError("Status 'pending' tidak ditemukan di database.");
    if (!user?.id)      return setError("User tidak ditemukan, silakan login ulang.");
    if (items.some(i => !i.item_name || !i.quantity))
      return setError("Nama barang dan jumlah wajib diisi untuk semua item.");

    try {
      setLoading(true);
      await api.post("/atk", {
        user_id:        user.id,
        status_id:      pendingStatus,
        request_date:   requestDate,
        requester_name: requesterName,
        department:     department,
        used_for:       usedFor     || null,
        request_type:   requestType,
        description:    description || null,
        items: items.map(i => ({
          item_name: i.item_name,
          quantity:  Number(i.quantity),
          satuan:    i.satuan,
        })),
      });
      setSuccess(true);
      const redirectPath = isAdmin ? `/${window.location.pathname.split("/")[1]}/atk` : "/user/atk";
      setTimeout(() => navigate(redirectPath), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Gagal membuat pengajuan ATK.");
    } finally { setLoading(false); }
  };

  const activeSifat = SIFAT_OPTIONS.find(s => s.value === requestType);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 960, mx: "auto" }}>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{
            border: `1px solid ${C.border}`, borderRadius: "8px", width: 32, height: 32,
            color: C.text2, backgroundColor: C.surface,
            "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
          }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.primaryBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EditNoteIcon sx={{ fontSize: 20, color: C.primary }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: C.text1, lineHeight: 1.2 }}>
                Form Pengajuan ATK
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
                Isi data pengajuan Alat Tulis Kantor
              </Typography>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}
            sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.dangerBdr}` }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success"
            sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem", border: `1px solid ${C.successBdr}` }}>
            Pengajuan ATK berhasil dibuat! Mengalihkan...
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>

          {/* Informasi Pemohon */}
          <Box sx={{ px: 2.5, py: "11px", borderBottom: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.text2,
              textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Informasi Pemohon
            </Typography>
          </Box>

          <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField fullWidth size="small" label="Nama Pemohon" required
                placeholder="Masukkan nama pemohon..."
                value={requesterName} onChange={(e) => setRequesterName(e.target.value)}
                sx={inputBase} />
              <TextField fullWidth size="small" label="Departemen / Unit" required
                placeholder="Masukkan nama departemen..."
                value={department} onChange={(e) => setDepartment(e.target.value)}
                sx={inputBase} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField fullWidth size="small" label="Digunakan Untuk"
                placeholder="Contoh: Operasional kantor, kegiatan seminar..."
                value={usedFor} onChange={(e) => setUsedFor(e.target.value)} sx={inputBase} />
              <TextField type="date" fullWidth size="small" label="Tanggal Diajukan"
                required InputLabelProps={{ shrink: true }}
                value={requestDate} onChange={(e) => setRequestDate(e.target.value)} sx={inputBase} />
            </Box>

            {/* Sifat Permintaan */}
            <Box>
              <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600,
                textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                Sifat Permintaan
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {SIFAT_OPTIONS.map((opt) => (
                  <Box key={opt.value} onClick={() => setRequestType(opt.value)} sx={{
                    display: "flex", alignItems: "center", gap: 0.75,
                    px: 1.75, py: "7px",
                    border: `1.5px solid ${requestType === opt.value ? opt.border : C.border}`,
                    borderRadius: "8px",
                    backgroundColor: requestType === opt.value ? opt.bg : C.surface,
                    cursor: "pointer", transition: "all 0.15s",
                    "&:hover": { borderColor: opt.border, backgroundColor: opt.bg },
                  }}>
                    <Box sx={{
                      width: 14, height: 14, borderRadius: "3px",
                      border: `1.5px solid ${requestType === opt.value ? opt.color : C.border}`,
                      backgroundColor: requestType === opt.value ? opt.color : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {requestType === opt.value && (
                        <Box component="span" sx={{
                          width: 6, height: 4,
                          borderLeft: "1.5px solid #fff", borderBottom: "1.5px solid #fff",
                          transform: "rotate(-45deg) translateY(-1px)", display: "block",
                        }} />
                      )}
                    </Box>
                    <Typography sx={{
                      fontSize: "0.8rem",
                      fontWeight: requestType === opt.value ? 600 : 400,
                      color: requestType === opt.value ? opt.color : C.text2,
                    }}>
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Daftar Barang ATK */}
          <Box sx={{ px: 2.5, py: "11px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            backgroundColor: C.surfaceAlt }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditNoteIcon sx={{ fontSize: 15, color: C.primary }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.text2,
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Daftar Barang ATK
              </Typography>
              <Box sx={{
                px: "7px", height: 18,
                backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
                borderRadius: "5px", display: "inline-flex", alignItems: "center",
              }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: C.primary }}>
                  {items.length} item
                </Typography>
              </Box>
            </Box>
            <Button size="small" variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
              onClick={addItem}
              sx={{
                height: 28, borderRadius: "7px", textTransform: "none",
                fontSize: "0.75rem", fontWeight: 600,
                borderColor: C.primary, color: C.primary,
                "&:hover": { backgroundColor: C.primaryBg },
              }}>
              Tambah
            </Button>
          </Box>

          {/* Table Header */}
          <Box sx={{
            display: { xs: "none", sm: "grid" },
            gridTemplateColumns: "32px 1fr 80px 130px 32px",
            gap: 1, px: 2.5, py: "8px",
            backgroundColor: "#F1F5F9", borderBottom: `1px solid ${C.border}`,
          }}>
            {["No", "Nama Barang ATK", "Jml", "Satuan", ""].map((h, i) => (
              <Typography key={i} sx={{ fontSize: "0.68rem", fontWeight: 700, color: C.text3,
                textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {h}
              </Typography>
            ))}
          </Box>

          <Box sx={{ px: 2.5 }}>
            {items.map((item, index) => (
              <ItemRow key={index} item={item} index={index} total={items.length}
                onChange={handleItemChange} onRemove={removeItem} atkOptions={atkOptions} />
            ))}
          </Box>

          <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
            <TextField multiline rows={2} fullWidth size="small"
              label="Catatan Tambahan (opsional)"
              placeholder="Keterangan umum untuk pengajuan ATK ini..."
              value={description} onChange={(e) => setDescription(e.target.value)}
              sx={inputBase} />
          </Box>

          <Divider />

          {/* Footer */}
          <Box sx={{
            px: 2.5, py: "14px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            flexWrap: "wrap", gap: 1,
            backgroundColor: C.surfaceAlt,
          }}>
            <Box sx={{
              display: "flex", alignItems: "center", gap: 0.75,
              px: 1.5, py: "5px",
              backgroundColor: activeSifat?.bg,
              border: `1px solid ${activeSifat?.border}`,
              borderRadius: "8px",
            }}>
              <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>Sifat:</Typography>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: activeSifat?.color }}>
                {activeSifat?.label}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}
                sx={{
                  borderRadius: "8px", textTransform: "none", height: 34,
                  borderColor: C.border, color: C.text2, fontSize: "0.82rem",
                  "&:hover": { borderColor: C.borderHov, backgroundColor: C.surfaceAlt },
                }}>
                Batal
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={loading || success}
                sx={{
                  borderRadius: "8px", textTransform: "none", height: 34, px: 3,
                  backgroundColor: C.primary, fontWeight: 600, fontSize: "0.82rem",
                  boxShadow: "0 1px 3px rgba(37,99,235,0.35)",
                  "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(37,99,235,0.4)" },
                }}>
                {loading ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Kirim Pengajuan ATK"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
